import { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useApp } from '../../context/AuthContext';
import { useAuthTimeout } from '../../hooks/useAuthTimeout';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { state } = useApp();
  const [isMobile, setIsMobile] = useState(false);
  
  // Use our auth timeout hook
  useAuthTimeout();

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Calculate margin based on screen size and sidebar state
  const getMainContentMargin = () => {
    if (isMobile) return 'ml-0';
    return state.sidebarCollapsed ? 'ml-16' : 'ml-64';
  };

  const handleOverlayClick = () => {
    // This will be handled by the dispatch in Sidebar component
  };

  const handleOverlayKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleOverlayClick();
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main content area */}
      <div 
        className={`flex-1 flex flex-col transition-all duration-300 ${getMainContentMargin()}`}
      >
        {/* Header */}
        <Header />
        
        {/* Main content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-8xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {isMobile && !state.sidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 !mt-0 z-40 lg:hidden"
          onClick={handleOverlayClick}
          onKeyDown={handleOverlayKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
        />
      )}
    </div>
  );
}
